import { useEffect } from "react";

interface UseLibraryPrefetchArgs {
  authToken: string | null;
  userId: string | undefined;
}

/**
 * Discover-integrations preload: fire once shortly after we have a signed-in
 * user + access token so the Library dialog renders instantly when the user
 * opens it. Uses requestIdleCallback (or a tiny setTimeout fallback) so we
 * don't compete with first paint.
 *
 * Extracted verbatim from AuthGate; the dependency array `[authToken, userId]`
 * is preserved exactly.
 */
export function useLibraryPrefetch({
  authToken,
  userId,
}: UseLibraryPrefetchArgs): void {
  useEffect(() => {
    if (!authToken || !userId) return;
    const idle: (cb: () => void) => number =
      typeof window !== "undefined" &&
      typeof (window as { requestIdleCallback?: (cb: () => void) => number })
        .requestIdleCallback === "function"
        ? (cb) =>
            (
              window as { requestIdleCallback: (cb: () => void) => number }
            ).requestIdleCallback(cb)
        : (cb) => window.setTimeout(cb, 1500);
    const cancel: (id: number) => void =
      typeof window !== "undefined" &&
      typeof (window as { cancelIdleCallback?: (id: number) => void })
        .cancelIdleCallback === "function"
        ? (id) =>
            (
              window as { cancelIdleCallback: (id: number) => void }
            ).cancelIdleCallback(id)
        : (id) => window.clearTimeout(id);
    const handle = idle(() => {
      void import("@/lib/library-prefetch").then(({ preloadLibrary }) => {
        preloadLibrary(authToken).catch(() => {
          // Swallow — the dialog still has its own fetch on open.
        });
      });
    });
    return () => cancel(handle);
  }, [authToken, userId]);
}
